import type { CopyByStep } from './mergeTourDefinitions.js';
import type { TourStructureManifest } from './tourStructureSchema.js';

export const TOUR_STRUCTURE_MANIFEST: TourStructureManifest = {
  structureVersion: 2,
  tours: [
    {
      slug: 'welcome_quick_start',
      name: 'Welcome Quick Start',
      description: 'A short introduction to APT and the Help menu.',
      tourType: 'welcome',
      requiredRoles: [],
      triggers: ['manual', 'first_login'],
      prerequisites: [],
      estimatedDurationMinutes: 2,
      steps: [
        {
          stepName: 'welcome_intro',
          target: 'body',
          placement: 'center',
        },
        {
          stepName: 'welcome_help',
          target: '[data-tour-stable="help-speed-dial"]',
          placement: 'left',
          beforeAction: 'openHelpSpeedDial',
        },
        {
          stepName: 'welcome_profile',
          target: '[data-tour-stable="nav-user-menu"]',
          placement: 'bottom',
          beforeAction: 'scrollIntoView',
        },
      ],
    },
    {
      slug: 'nav_reference',
      name: 'Navigation Reference',
      description: 'Top-level navigation overview (manual only).',
      tourType: 'reference',
      requiredRoles: [],
      triggers: ['manual'],
      prerequisites: [],
      estimatedDurationMinutes: 8,
      steps: [
        { stepName: 'nav_home', target: '[data-tour-stable="nav-home"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_coordinator',
      name: 'Coordinator Dashboard',
      tourType: 'task',
      requiredRoles: ['coordinator'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 3,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'coordinator_dashboard_overview', target: '[data-tour="coordinator-tab-strip"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_attendee',
      name: 'Attendee Dashboard',
      tourType: 'task',
      requiredRoles: ['attendee'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 6,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'attendee_events_tab', target: '[data-tour="attendee-tab-events"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_training_manager',
      name: 'Training Manager Dashboard',
      tourType: 'task',
      requiredRoles: ['training_manager'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'tm_events_tab', target: '[data-tour="training-manager-tab-events"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_supervisor',
      name: 'Supervisor Dashboard',
      tourType: 'task',
      requiredRoles: ['supervisor'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 4,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'supervisor_activity_tab', target: '[data-tour="supervisor-tab-activity"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_instructor',
      name: 'Instructor Dashboard',
      tourType: 'task',
      requiredRoles: ['instructor'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 3,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'instructor_manage_bio', target: '[data-tour="instructor-manage-bio"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_tacticore',
      name: 'TactiCore Dashboard',
      tourType: 'task',
      requiredRoles: ['tacticore'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 2,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'dashboard_role_tab_tacticore', target: '[data-tour-stable="role-tab-tacticore"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'dashboard_k9_ops',
      name: 'APEX K9 Dashboard',
      tourType: 'task',
      requiredRoles: ['k9_handler', 'k9_supervisor', 'k9_tech', 'k9_admin'],
      triggers: ['manual', 'route:/dashboard'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 2,
      steps: [
        { stepName: 'dash_welcome', target: '[data-tour-stable="dashboard-welcome"]', placement: 'bottom' },
        { stepName: 'dashboard_role_tab_k9_ops', target: '[data-tour-stable="role-tab-k9_ops"]', placement: 'bottom' },
      ],
    },
    {
      slug: 'coordinator_create_event',
      name: 'Create an Event (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      // Lab missions start from Help menu only — playbook steps expand at runtime.
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 8,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_cancel_event',
      name: 'Cancel an Event (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 6,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_create_user',
      name: 'Add Agency Members (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_manage_venues',
      name: 'Manage Venues (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_embed_calendar',
      name: 'Embed Calendar (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 6,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_agency_profile',
      name: 'Agency Profile (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_certificates',
      name: 'Certificates (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'coordinator_outside_training',
      name: 'Outside Training Review (Learning Lab)',
      tourType: 'task',
      delivery: 'learning_lab',
      requiredRoles: ['coordinator'],
      triggers: ['manual'],
      prerequisites: ['welcome_quick_start', 'dashboard_coordinator'],
      estimatedDurationMinutes: 6,
      steps: [
        { stepName: 'lab_enter', target: 'body', placement: 'center', beforeAction: 'enterLearningLab' },
      ],
    },
    {
      slug: 'author_publish_first_post',
      name: 'Publish your first blog post',
      description: 'Create a draft and submit it for review from My blog posts.',
      tourType: 'task',
      requiredRoles: [],
      triggers: ['manual', 'route:/blog/manage', 'route:/manage'],
      prerequisites: ['welcome_quick_start'],
      estimatedDurationMinutes: 5,
      steps: [
        { stepName: 'author_manage', target: '[data-tour="blog-manage-page"]', placement: 'bottom' },
        { stepName: 'author_new', target: '[data-tour="blog-manage-new"]', placement: 'bottom' },
        { stepName: 'author_editor', target: '[data-tour="blog-editor-page"]', placement: 'bottom' },
      ],
    },
  ],
};

export const DEFAULT_TOUR_COPY: Record<string, CopyByStep> = {
  welcome_quick_start: {
    welcome_intro: {
      title: 'Welcome to American Police Training',
      content:
        'APT connects agencies, instructors, and officers for training discovery, registration, and compliance. Use the Help button anytime to reopen guided tours.',
    },
    welcome_help: {
      title: 'Help is always here',
      content:
        'Open the Help menu to browse tours, contact support, and find answers without leaving your workflow.',
    },
    welcome_profile: {
      title: 'Your account menu',
      content:
        'Manage profile settings, notifications, and dashboard access from the user menu in the header.',
    },
  },
  nav_reference: {
    nav_logo: {
      title: 'APT logo',
      content: 'Return to the APT home page from the logo anywhere on the site.',
    },
    nav_home: { title: 'Home', content: 'Open the home page from the main navigation bar.' },
    nav_tac_shop: { title: 'Tac-Shop', content: 'Browse tactical gear and training supplies.' },
    nav_browse: { title: 'Browse', content: 'Open the browse menu for events, agencies, venues, and more.' },
    nav_about: { title: 'About Us', content: 'Company information, contact options, and policies for visitors.' },
    nav_leaderboard: {
      title: 'Leaderboard',
      content: 'See training engagement rankings when enabled for your account.',
    },
    nav_notifications: { title: 'Notifications', content: 'Review alerts and messages in one place.' },
    nav_user_menu: { title: 'User menu', content: 'Account shortcuts, settings, and sign out.' },
    nav_mobile: {
      title: 'Mobile menu',
      content: 'On smaller screens, open the menu to reach the same links as the desktop navigation bar.',
    },
  },
  dashboard_coordinator: {
    dash_welcome: { title: 'Coordinator dashboard', content: 'Manage payouts, events, and agency operations from here.' },
    coordinator_dashboard_overview: {
      title: 'Coordinator dashboard tabs',
      content:
        'Tabs below organize agency tools—use Learning Lab to practice workflows hands-on.',
    },
    coordinator_payouts_tab: { title: 'Payouts & Funds', content: 'Track Stripe payout readiness and agency fund settings.' },
    coordinator_payout_status: { title: 'Payout status', content: 'Track Stripe payout readiness for your agency.' },
  },
  dashboard_attendee: {
    dash_welcome: { title: 'Attendee dashboard', content: 'Track your events, certificates, and training progress.' },
    attendee_events_tab: { title: 'My Events', content: 'View registered and upcoming training events.' },
    attendee_geo_alerts: {
      title: 'Training geo alerts',
      content: 'Configure location-based alerts for training opportunities near you.',
    },
    attendee_certificates_tab: { title: 'Certificates', content: 'Download and review earned certificates.' },
    attendee_outside_training_tab: {
      title: 'Outside Training',
      content: 'Log and track training completed outside APT.',
    },
    attendee_badges_tab: {
      title: 'Badges',
      content: 'See achievement badges you have earned from training activity.',
    },
    attendee_leaderboard_tab: {
      title: 'Leader Board',
      content: 'See training engagement rankings when enabled.',
    },
    attendee_secondary_employment_tab: {
      title: 'Secondary Employment',
      content: 'Browse secondary employment listings and manage your applications.',
    },
    attendee_se_applications: {
      title: 'My applications',
      content: 'Track listings you have applied to and their status.',
    },
    attendee_se_assignments: {
      title: 'My assignments',
      content: 'Review secondary employment assignments linked to your account.',
    },
  },
  dashboard_training_manager: {
    dash_welcome: { title: 'Training manager dashboard', content: 'Oversee instructors, venues, and scheduled events.' },
    tm_events_tab: { title: 'My Events', content: 'Manage training events for your organization.' },
    tm_instructors_tab: { title: 'Instructors', content: 'Review and assign instructors to courses.' },
    tm_tasks_tab: { title: 'Tasks', content: 'Track assignments and follow-ups for your training program.' },
    tm_notifications_tab: { title: 'Notifications', content: 'Review alerts and updates for your training operations.' },
    tm_venues_tab: { title: 'Venues', content: 'Maintain venue records used by your events.' },
  },
  dashboard_supervisor: {
    dash_welcome: { title: 'Supervisor dashboard', content: 'Supervise your team, review activity, and manage registrations.' },
    supervisor_activity_tab: { title: 'Activity Overview', content: 'Review recent team activity notices and priorities.' },
    supervisor_team_tab: { title: 'Team Members', content: 'Search, filter, and manage members on your team.' },
    supervisor_add_members_tab: { title: 'Add Members', content: 'Invite or add officers to your supervised team.' },
  },
  dashboard_instructor: {
    dash_welcome: { title: 'Instructor dashboard', content: 'Manage your classes, rosters, and instructor profile.' },
    instructor_manage_bio: { title: 'Manage Instructor Bio', content: 'Update your public instructor profile and credentials.' },
    instructor_stats: { title: 'Instructor stats', content: 'Quick snapshot of your events, students, ratings, and materials.' },
    instructor_events: { title: 'Your events', content: 'View, manage, and open the events you teach.' },
  },
  dashboard_tacticore: {
    dash_welcome: { title: 'TactiCore on APT', content: 'Access TactiCore lesson planning and exam tools from your dashboard.' },
    dashboard_role_tab_tacticore: {
      title: 'TactiCore™ workspace',
      content:
        'Open TactiCore to build lesson plans, create exams, and manage organization content in its dedicated workspace.',
    },
  },
  dashboard_k9_ops: {
    dash_welcome: { title: 'APEX K9 on APT', content: 'Jump to K9 operations from your role dashboard tabs.' },
    dashboard_role_tab_k9_ops: {
      title: 'APEX K9 operations',
      content:
        'Open the K9 operations dashboard for tracking, training records, and field workflows.',
    },
  },
  coordinator_create_event: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Practice with sample agency data. Your real agency is never changed.',
    },
  },
  coordinator_cancel_event: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Practice canceling an event with sample data — saves are blocked in lab mode.',
    },
  },
  coordinator_create_user: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Practice adding agency members without changing your real roster.',
    },
  },
  coordinator_manage_venues: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Explore venue management with pre-seeded locations.',
    },
  },
  coordinator_embed_calendar: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Practice embed settings with a sample calendar configuration.',
    },
  },
  coordinator_agency_profile: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Review agency profile fields using sample agency data.',
    },
  },
  coordinator_certificates: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Explore certificate templates and issued records in practice mode.',
    },
  },
  coordinator_outside_training: {
    lab_enter: {
      title: 'Enter the Learning Lab',
      content: 'Practice reviewing outside training with a seeded pending submission.',
    },
  },
  author_publish_first_post: {
    author_manage: {
      title: 'Your blog posts',
      content: 'Manage drafts, pending reviews, and published articles here.',
    },
    author_new: {
      title: 'Create a post',
      content: 'Start a new article with the full-page editor.',
    },
    author_editor: {
      title: 'Write and submit',
      content: 'Add title, content, featured image, and SEO fields, then save a draft or submit for review.',
    },
  },
};
